import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Award, Users, Heart, ShieldCheck, Star, Quote, MapPin, CalendarCheck, Sparkles, BookOpen, Languages } from "lucide-react";
import { Button } from "../components/ui/button";
import { PROGRAMS, INSTRUCTOR, TESTIMONIALS, IMAGES, CONTACT, AREAS, DISCIPLINES, GOOGLE_RATING, FAQS, TRYOUT } from "../mock";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import Seo, { PROFESSOR_JSONLD } from "../components/Seo";
import HalloweenBanner from "../components/HalloweenBanner";

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) e.target.classList.add("is-visible");
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const Home = () => {
  useReveal();

  return (
    <div>
      <Seo
        title="Brazilian Jiu-Jitsu Alameda | Kids Martial Arts, Self-Defense & Adult BJJ — Alameda BJJ"
        description="Brazilian Jiu-Jitsu, kids martial arts, toddler motor fitness, teen & adult BJJ, practical self-defense and Verbal Jiu-Jitsu in Alameda, CA. Taught by 5th-degree black belt Professor Sergio Silva. Book your first class free."
        path="/"
        jsonLd={PROFESSOR_JSONLD}
      />
      {/* HERO */}
      <section className="relative min-h-[92vh] flex items-center pt-24 overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.hero} alt="Brazilian Jiu-Jitsu training in Alameda" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0E1613]/95 via-[#0E1613]/80 to-[#0E1613]/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full">
          <div className="max-w-2xl animate-fade-up">
            <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-4 py-1.5 text-xs font-medium text-white tracking-wide border border-white/15">
              <span className="h-2 w-2 rounded-full bg-br-yellow" /> Alameda, California • Since 2008
            </span>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08]">
              Build Unshakable Confidence Through Jiu-Jitsu
            </h1>
            <p className="mt-6 text-lg text-neutral-200 leading-relaxed max-w-xl">
              World-class Brazilian Jiu-Jitsu & Self-Defense for toddlers, kids, teens and adults — taught by World Champion Professor Sergio Silva.
            </p>
            <Link to="/trial" data-testid="hero-free-trial-btn" className="block mt-8">
              <Button className="w-full sm:w-auto h-14 px-10 bg-br-green hover:bg-[#00822f] text-white rounded-full text-base sm:text-lg font-extrabold shadow-lg shadow-black/20">
                <CalendarCheck size={20} className="mr-2" /> BOOK YOUR FIRST CLASS FREE
              </Button>
            </Link>
            <div className="mt-4 rounded-2xl bg-white/10 backdrop-blur border border-br-yellow/50 p-4 sm:p-5" data-testid="hero-tryout-card">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <span className="text-[11px] font-bold tracking-[0.18em] text-br-yellow uppercase">New Student Offer</span>
                  <p className="mt-1 font-display text-xl sm:text-2xl font-extrabold text-white leading-tight">{TRYOUT.name}</p>
                  <p className="mt-1 text-sm text-neutral-200">1 month · 2 classes/week · FREE uniform · one-time payment, no auto-renewal</p>
                </div>
                <span className="font-display text-3xl sm:text-4xl font-extrabold text-br-yellow shrink-0">{TRYOUT.price}</span>
              </div>
              <Link to="/tryout" data-testid="hero-enroll-99-btn">
                <Button className="mt-4 w-full h-12 bg-br-yellow hover:bg-[#e6c900] text-[#1A1A1A] rounded-full text-sm font-extrabold">
                  CLAIM THE {TRYOUT.price} TRYOUT <ArrowRight size={17} className="ml-1" />
                </Button>
              </Link>
            </div>
            <div className="mt-4 grid grid-cols-2 sm:flex sm:flex-wrap gap-2.5">
              <Link to="/pricing#challenge" data-testid="hero-challenge-btn">
                <Button variant="outline" className="h-11 px-4 bg-white/10 backdrop-blur border-white/50 text-white hover:bg-white hover:text-[#1A1A1A] rounded-full text-xs sm:text-sm font-bold w-full">
                  <Sparkles size={15} className="mr-1" /> $199 Kids Challenge
                </Button>
              </Link>
              <Link to="/pricing#memberships" data-testid="hero-web-specials-btn">
                <Button variant="outline" className="h-11 px-4 bg-white/10 backdrop-blur border-white/50 text-white hover:bg-white hover:text-[#1A1A1A] rounded-full text-xs sm:text-sm font-bold w-full">
                  Membership Plans
                </Button>
              </Link>
              <Link to="/pricing#private" data-testid="hero-private-btn">
                <Button variant="outline" className="h-11 px-4 bg-white/10 backdrop-blur border-white/50 text-white hover:bg-white hover:text-[#1A1A1A] rounded-full text-xs sm:text-sm font-bold w-full">
                  Private Lesson
                </Button>
              </Link>
              <Link to="/schedule" data-testid="hero-class-schedule-btn">
                <Button variant="outline" className="h-11 px-4 bg-white/10 backdrop-blur border-white/50 text-white hover:bg-white hover:text-[#1A1A1A] rounded-full text-xs sm:text-sm font-bold w-full">
                  Class Schedule
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-4 text-sm font-semibold text-br-yellow">
              <Link to="/es" data-testid="hero-es-link" className="inline-flex items-center gap-1.5 hover:underline"><Languages size={15} /> Se habla español</Link>
              <Link to="/pt" data-testid="hero-pt-link" className="inline-flex items-center gap-1.5 hover:underline"><Languages size={15} /> Falamos português</Link>
            </div>
            <div className="mt-10 flex flex-wrap gap-x-8 gap-y-3 text-sm text-neutral-300">
              <span className="flex items-center gap-2"><Award size={16} className="text-br-yellow" /> World Champion Instruction</span>
              <span className="flex items-center gap-2"><Users size={16} className="text-br-yellow" /> 17+ Black Belts Developed</span>
              <span className="flex items-center gap-2"><Heart size={16} className="text-br-yellow" /> Family Friendly</span>
            </div>
          </div>
        </div>
      </section>

      <HalloweenBanner />

      {/* WELCOME */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal relative">
            <img src={IMAGES.dojo} alt="Students training at Alameda BJJ dojo" className="rounded-2xl w-full h-[460px] object-cover shadow-xl" />
            <div className="absolute -bottom-6 -right-4 sm:right-6 bg-white rounded-xl shadow-lg p-5 w-48 border border-neutral-100">
              <p className="font-display text-3xl font-extrabold text-br-green">Since 1990</p>
              <p className="text-sm text-neutral-600 mt-1">Teaching martial arts in Alameda</p>
            </div>
          </div>
          <div className="reveal">
            <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Welcome</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A] leading-tight">
              Real Confidence, One Class at a Time
            </h2>
            <p className="mt-5 text-neutral-600 leading-relaxed">
              At Alameda Brazilian Jiu-Jitsu & Self-Defense Academy, we help kids, teens, and adults discover what real confidence feels like. Students learn to stay calm under pressure, think clearly in challenges, and move with purpose — on and off the mats.
            </p>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Beyond practical self-defense and anti-bullying skills, students develop Verbal Jiu-Jitsu™ — a unique communication system to recognize conflict early, defuse tension, and protect what matters most.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              {[
                { icon: ShieldCheck, t: "Safe & Motivating", d: "A positive space for every age." },
                { icon: Heart, t: "Get in Great Shape", d: "Burn calories while you learn." },
              ].map((it, i) => (
                <div key={i} className="flex gap-3">
                  <div className="h-10 w-10 rounded-lg bg-br-green/10 flex items-center justify-center shrink-0">
                    <it.icon className="text-br-green" size={20} />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1A1A1A] text-sm">{it.t}</p>
                    <p className="text-sm text-neutral-500">{it.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="section-pad bg-[#F4F5F1]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto reveal">
            <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Programs</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A]">Classes for Every Age & Goal</h2>
            <p className="mt-4 text-neutral-600">From toddlers taking their first steps on the mat to adults chasing their best shape — there's a place for you here.</p>
          </div>
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-7">
            {PROGRAMS.map((p) => (
              <Link to="/programs" key={p.id} className="reveal group bg-white rounded-2xl overflow-hidden border border-neutral-100 card-hover">
                <div className="h-52 overflow-hidden">
                  <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />
                </div>
                <div className="p-6">
                  <span className="text-xs font-semibold text-br-green tracking-wide">{p.ages}</span>
                  <h3 className="mt-1 font-display text-xl font-bold text-[#1A1A1A]">{p.title}</h3>
                  <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{p.short}</p>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-br-green">
                    Learn more <ArrowRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INSTRUCTOR */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <div className="reveal order-2 lg:order-1">
            <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Your Instructor</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A]">{INSTRUCTOR.name}</h2>
            <p className="mt-2 text-br-blue font-semibold">{INSTRUCTOR.title}</p>
            <p className="mt-5 text-neutral-600 leading-relaxed">{INSTRUCTOR.bio[0]}</p>
            <div className="mt-6 grid sm:grid-cols-2 gap-3">
              {INSTRUCTOR.achievements.slice(0, 4).map((a, i) => (
                <div key={i} className="flex items-start gap-2.5 text-sm text-neutral-700">
                  <Award size={17} className="text-br-yellow shrink-0 mt-0.5" /> {a}
                </div>
              ))}
            </div>
            <Link to="/about">
              <Button className="mt-8 bg-[#1A1A1A] hover:bg-black text-white rounded-full px-7">Read Full Story</Button>
            </Link>
          </div>
          <div className="reveal order-1 lg:order-2">
            <img src={INSTRUCTOR.image} alt={INSTRUCTOR.name} className="rounded-2xl w-full h-[480px] object-cover shadow-xl" />
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="section-pad bg-[#0E1613]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="text-center max-w-2xl mx-auto reveal">
            <span className="text-sm font-semibold tracking-[0.2em] text-br-yellow uppercase">Community</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-white">Loved by Alameda Families</h2>
            <div className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/5 border border-white/10 px-4 py-2">
              <div className="flex gap-0.5">
                {[...Array(5)].map((_, s) => <Star key={s} size={16} className="fill-br-yellow text-br-yellow" />)}
              </div>
              <span className="text-sm text-neutral-200">{GOOGLE_RATING.stars}.0 on Google · {GOOGLE_RATING.count} reviews</span>
            </div>
          </div>
          <div className="mt-12 grid md:grid-cols-3 gap-7">
            {TESTIMONIALS.slice(0, 6).map((t, i) => (
              <div key={i} className="reveal bg-white/5 border border-white/10 rounded-2xl p-7">
                <Quote className="text-br-yellow" size={28} />
                <div className="flex gap-1 mt-4">
                  {[...Array(5)].map((_, s) => <Star key={s} size={15} className="fill-br-yellow text-br-yellow" />)}
                </div>
                <p className="mt-4 text-neutral-200 leading-relaxed">“{t.quote}”</p>
                <div className="mt-6">
                  <p className="font-semibold text-white">{t.name}</p>
                  <p className="text-sm text-neutral-400">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="reveal text-center mt-10">
            <Link to="/reviews">
              <Button variant="outline" className="h-12 px-8 bg-transparent border-white/40 text-white hover:bg-white hover:text-[#1A1A1A] rounded-full font-semibold">
                Read All Reviews
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* GET STARTED IN 3 STEPS */}
      <section className="section-pad bg-[#F4F5F1]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-2xl mx-auto">
            <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Getting Started</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A]">Start Training in 3 Easy Steps</h2>
            <p className="mt-4 text-neutral-600">No experience needed. We make your first day simple and welcoming.</p>
          </div>
          <div className="mt-14 grid md:grid-cols-3 gap-7">
            {[
              { icon: CalendarCheck, step: "01", t: "Book Your Free Trial", d: "Pick a day that works for you and reserve a free, no-commitment class in under a minute." },
              { icon: Users, step: "02", t: "Come Train With Us", d: "Wear comfortable clothes and show up — we'll guide you through everything, step by step." },
              { icon: Sparkles, step: "03", t: "Choose Your Plan & Grow", d: "Love it? Pick a membership and start building confidence, fitness and real skills." },
            ].map((s, i) => (
              <div key={i} className="reveal relative bg-white rounded-2xl border border-neutral-100 p-8 card-hover">
                <span className="absolute top-6 right-6 font-display text-4xl font-extrabold text-neutral-100">{s.step}</span>
                <div className="h-12 w-12 rounded-xl bg-br-green/10 flex items-center justify-center">
                  <s.icon className="text-br-green" size={24} />
                </div>
                <h3 className="mt-5 font-display text-lg font-bold text-[#1A1A1A]">{s.t}</h3>
                <p className="mt-2 text-sm text-neutral-600 leading-relaxed">{s.d}</p>
              </div>
            ))}
          </div>
          <div className="reveal text-center mt-10">
            <Link to="/trial">
              <Button className="h-12 px-8 bg-br-green hover:bg-[#00822f] text-white rounded-full font-semibold">
                Book Your Free Trial <ArrowRight size={18} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* AREAS WE SERVE (SEO) */}
      <section className="section-pad">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center max-w-3xl mx-auto">
            <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">Serving the East Bay</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A]">
              Brazilian Jiu-Jitsu & Martial Arts for the Whole Bay Area
            </h2>
            <p className="mt-4 text-neutral-600 leading-relaxed">
              Conveniently located in Alameda, our academy proudly welcomes students from across the East Bay — including
              Oakland, San Leandro and Emeryville. Whether you're searching for Brazilian Jiu-Jitsu, Submission Wrestling,
              Mixed Martial Arts, kids martial arts, teens martial arts, self-defense or adult classes, you'll find
              world-class instruction and a family-friendly community here.
            </p>
          </div>
          <div className="reveal mt-9 flex flex-wrap justify-center gap-2.5">
            {AREAS.map((a) => (
              <span key={a} className="inline-flex items-center gap-1.5 rounded-full bg-br-green/10 text-br-green px-4 py-1.5 text-sm font-semibold">
                <MapPin size={14} /> {a}
              </span>
            ))}
          </div>
          <div className="reveal mt-4 flex flex-wrap justify-center gap-2">
            {DISCIPLINES.map((d) => (
              <span key={d} className="inline-flex rounded-full border border-neutral-200 bg-white px-3.5 py-1.5 text-xs font-medium text-neutral-600">
                {d}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* FREE EBOOK / NEWSLETTER */}
      <section className="py-12 bg-[#F4F5F1]">
        <div className="max-w-5xl mx-auto px-5 lg:px-8">
          <div className="reveal rounded-3xl bg-white border border-neutral-100 p-7 sm:p-10 flex flex-col md:flex-row md:items-center gap-6 shadow-sm" data-testid="home-ebook-cta">
            <div className="h-16 w-16 rounded-2xl bg-br-green/10 flex items-center justify-center shrink-0">
              <BookOpen className="text-br-green" size={30} />
            </div>
            <div className="flex-1">
              <span className="text-xs font-bold tracking-[0.18em] text-br-green uppercase">Free eBook + Newsletter</span>
              <h2 className="mt-1 font-display text-2xl sm:text-3xl font-bold text-[#1A1A1A]">The Beginner's Guide to Brazilian Jiu-Jitsu & Verbal Jiu-Jitsu™</h2>
              <p className="mt-2 text-neutral-600">Join our newsletter and get Professor Silva's free guide to BJJ, practical self-defense and de-escalating conflict with words — plus class news and member specials.</p>
            </div>
            <Link to="/newsletter" data-testid="home-ebook-btn" className="shrink-0">
              <Button className="h-12 px-7 bg-br-green hover:bg-[#00822f] text-white rounded-full font-bold w-full md:w-auto">
                Get the Free eBook <ArrowRight size={17} className="ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>


      {/* FAQ */}
      <section className="section-pad">
        <div className="max-w-3xl mx-auto px-5 lg:px-8">
          <div className="reveal text-center">
            <span className="text-sm font-semibold tracking-[0.2em] text-br-green uppercase">FAQ</span>
            <h2 className="mt-3 font-display text-3xl sm:text-4xl font-bold text-[#1A1A1A]">Common Questions</h2>
            <p className="mt-4 text-neutral-600">Everything you need to know before your first free class.</p>
          </div>
          <div className="reveal mt-10">
            <Accordion type="single" collapsible className="w-full">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-b border-neutral-200">
                  <AccordionTrigger className="text-left font-semibold text-[#1A1A1A] hover:no-underline">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-neutral-600 leading-relaxed">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
          <div className="reveal text-center mt-8">
            <Link to="/trial"><Button className="h-12 px-8 bg-br-green hover:bg-[#00822f] text-white rounded-full font-semibold">Book a Free Class</Button></Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20">
        <div className="max-w-6xl mx-auto px-5 lg:px-8">
          <div className="reveal rounded-3xl bg-br-green px-8 py-14 sm:px-14 text-center relative overflow-hidden">
            <div className="absolute top-0 right-0 h-40 w-40 bg-br-yellow/20 rounded-full blur-3xl" />
            <h2 className="font-display text-3xl sm:text-4xl font-extrabold text-white relative">Ready to Step on the Mat?</h2>
            <p className="mt-4 text-green-50 max-w-xl mx-auto relative">Start your trial today and discover why families across Alameda choose us. First class is on us.</p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center relative">
              <Link to="/trial"><Button className="h-12 px-8 bg-white text-br-green hover:bg-neutral-100 rounded-full font-semibold">Start Free Trial</Button></Link>
              <a href={CONTACT.phoneHref}><Button variant="outline" className="h-12 px-8 bg-transparent border-white text-white hover:bg-white hover:text-br-green rounded-full font-semibold">Call {CONTACT.phone}</Button></a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
